import React, { useEffect, useState, useMemo } from "react";
import { api, apiErrorMessage, formatCurrency, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, X, ShoppingCart, DownloadSimple, FileText, Coins, WhatsappLogo, ClipboardText } from "@phosphor-icons/react";
import { useNavigate, useLocation } from "react-router-dom";
import ReceivePaymentDialog from "@/components/ReceivePaymentDialog";
import { buildWhatsAppUrl, invoiceShareMessage } from "@/lib/whatsapp";
import { Select as StatusSelect, SelectContent as SC, SelectItem as SI, SelectTrigger as ST, SelectValue as SV } from "@/components/ui/select";
import OrdersCardView from "@/components/OrdersCardView";

const STATUS_STYLES = {
    draft:     { bg: "#F3F4F6", fg: "#525252" },
    pending:   { bg: "#FEF3C7", fg: "#B45309" },
    completed: { bg: "#DCFCE7", fg: "#15803D" },
    cancelled: { bg: "#FEE2E2", fg: "#DC2626" },
};
const PAYMENT_STYLES = {
    unpaid:  { bg: "#FEE2E2", fg: "#DC2626" },
    partial: { bg: "#FEF3C7", fg: "#B45309" },
    paid:    { bg: "#DCFCE7", fg: "#15803D" },
};

export default function Sales() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const isOrdersView = location.pathname.startsWith("/orders");
    const [sales, setSales] = useState([]);
    const [products, setProducts] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState({ customer_id: "", items: [], discount: 0, status: "completed", payment_status: "paid", notes: "" });
    const [saving, setSaving] = useState(false);
    const [paymentSaleId, setPaymentSaleId] = useState(null);
    const [statusFilter, setStatusFilter] = useState(isOrdersView ? "active" : "all");

    // React to path changes (sidebar clicks between /orders and /sales)
    useEffect(() => {
        setStatusFilter(isOrdersView ? "active" : "all");
    }, [isOrdersView]);

    const load = async () => {
        setLoading(true);
        try {
            const [s, p, c] = await Promise.all([
                api.get("/sales"),
                api.get("/products", { params: { limit: 500, is_active: true } }),
                api.get("/customers"),
            ]);
            setSales(s.data);
            setProducts(p.data);
            setCustomers(c.data);
        } catch (e) { toast.error(apiErrorMessage(e)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setForm({
            customer_id: "",
            items: [],
            discount: 0,
            status: isOrdersView ? "pending" : "completed",
            payment_status: isOrdersView ? "unpaid" : "paid",
            notes: "",
        });
        setDialogOpen(true);
    };

    const visibleSales = sales.filter((o) => {
        if (statusFilter === "all") return true;
        if (statusFilter === "active") return o.status === "draft" || o.status === "pending";
        return o.status === statusFilter;
    });

    const addItem = () => {
        setForm((f) => ({ ...f, items: [...f.items, { product_id: "", quantity: 1, unit_price: 0, gst_percent: 18 }] }));
    };
    const updateItem = (idx, patch) => {
        setForm((f) => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
    };
    const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

    const onProductSelect = (idx, productId) => {
        const p = products.find((x) => x.id === productId);
        updateItem(idx, {
            product_id: productId,
            unit_price: p ? Number(p.selling_price || 0) : 0,
            gst_percent: p ? Number(p.gst_percent || 0) : 18,
        });
    };

    const totals = useMemo(() => {
        let subtotal = 0, tax = 0;
        for (const it of form.items) {
            const line = Number(it.quantity || 0) * Number(it.unit_price || 0);
            subtotal += line;
            tax += line * Number(it.gst_percent || 0) / 100;
        }
        const total = subtotal + tax - Number(form.discount || 0);
        return { subtotal, tax, total };
    }, [form.items, form.discount]);

    const submit = async () => {
        if (form.items.length === 0) return toast.error("Add at least one item");
        if (form.items.some((it) => !it.product_id)) return toast.error("Select product for all items");
        setSaving(true);
        try {
            await api.post("/sales", {
                customer_id: form.customer_id || null,
                items: form.items.map((it) => ({
                    product_id: it.product_id,
                    quantity: Number(it.quantity || 0),
                    unit_price: Number(it.unit_price || 0),
                    gst_percent: Number(it.gst_percent || 0),
                })),
                discount: Number(form.discount || 0),
                status: form.status,
                payment_status: form.payment_status,
                notes: form.notes,
            });
            toast.success("Sale recorded");
            setDialogOpen(false);
            load();
        } catch (e) { toast.error(apiErrorMessage(e)); } finally { setSaving(false); }
    };

    const changeStatus = async (order, newStatus) => {
        try {
            await api.patch(`/sales/${order.id}/status`, { status: newStatus });
            toast.success(`Order marked ${newStatus}`);
            load();
        } catch (e) { toast.error(apiErrorMessage(e)); }
    };

    const shareInvoice = (o) => {
        const cust = customers.find((c) => c.id === o.customer_id);
        const msg = invoiceShareMessage({
            customerName: o.customer_name,
            invoiceNo: o.order_no,
            total: o.total,
            invoiceUrl: `${window.location.origin}/invoice/${o.id}`,
        });
        window.open(buildWhatsAppUrl(cust?.whatsapp || cust?.mobile, msg), "_blank");
    };

    const exportCsv = () => {
        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/export?kind=sales`, "_blank");
    };

    return (
        <>
            {isOrdersView ? (
                <OrdersCardView onCreate={openCreate} />
            ) : (
        <div className="space-y-6" data-testid="sales-page">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <div className="eyebrow mb-2">Revenue</div>
                    <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight font-display">Sales & Orders</h1>
                    <p className="text-sm text-neutral-500 mt-1.5">{sales.length} order{sales.length !== 1 && "s"} recorded.</p>
                </div>
                <div className="flex gap-2">
                    <StatusSelect value={statusFilter} onValueChange={setStatusFilter}>
                        <ST className="w-40 h-11" data-testid="sales-status-filter"><SV /></ST>
                        <SC>
                            <SI value="all">All Statuses</SI>
                            <SI value="active">Active (Draft + Pending)</SI>
                            <SI value="draft">Draft</SI>
                            <SI value="pending">Pending</SI>
                            <SI value="completed">Completed</SI>
                            <SI value="cancelled">Cancelled</SI>
                        </SC>
                    </StatusSelect>
                    <Button variant="outline" onClick={exportCsv} data-testid="sales-export-btn"><DownloadSimple size={14} className="mr-1.5" /> CSV</Button>
                    <Button onClick={openCreate} className="text-white h-11" style={{ backgroundColor: "#15803D" }} data-testid="sales-add-btn">
                        <Plus size={16} weight="bold" className="mr-1.5" /> New Sale
                    </Button>
                </div>
            </div>

            <div className="bg-white border border-neutral-200 rounded-md overflow-hidden" data-testid="sales-table">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                            <tr>
                                <th className="text-left px-5 py-3 font-semibold">Order</th>
                                <th className="text-left px-5 py-3 font-semibold">Customer</th>
                                <th className="text-left px-5 py-3 font-semibold">Items</th>
                                <th className="text-right px-5 py-3 font-semibold">Total</th>
                                <th className="text-left px-5 py-3 font-semibold">Status</th>
                                <th className="text-left px-5 py-3 font-semibold">Payment</th>
                                <th className="text-left px-5 py-3 font-semibold">Date</th>
                                <th className="text-right px-5 py-3 font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={8} className="text-center py-10 text-neutral-400">Loading...</td></tr>}
                            {!loading && visibleSales.length === 0 && (
                                <tr><td colSpan={8} className="text-center py-10 text-neutral-400">
                                    {isOrdersView ? "No active orders. Create a new order to get started." : "No sales yet. Create your first sale."}
                                </td></tr>
                            )}
                            {visibleSales.map((o) => (
                                <tr key={o.id} className="data-row border-t border-neutral-100" data-testid={`sale-row-${o.order_no}`}>
                                    <td className="px-5 py-3 font-mono text-xs font-semibold">
                                        <button onClick={() => navigate(`/orders/${o.id}`)} className="hover:underline" data-testid={`sale-open-${o.order_no}`}>{o.order_no}</button>
                                    </td>
                                    <td className="px-5 py-3 text-neutral-800">{o.customer_name}</td>
                                    <td className="px-5 py-3 text-neutral-500 text-xs">{o.items.length} item{o.items.length !== 1 && "s"}</td>
                                    <td className="px-5 py-3 text-right font-bold tabular font-display">{formatCurrency(o.total)}</td>
                                    <td className="px-5 py-3">
                                        <StatusPill label={o.status} styleMap={STATUS_STYLES} />
                                        {o.fulfillment_stage && o.fulfillment_stage !== "new" && (
                                            <div className="mt-1 text-[9px] font-bold uppercase tracking-wider text-neutral-500">{o.fulfillment_stage}</div>
                                        )}
                                    </td>
                                    <td className="px-5 py-3"><StatusPill label={o.payment_status} styleMap={PAYMENT_STYLES} /></td>
                                    <td className="px-5 py-3 text-neutral-500 text-xs whitespace-nowrap">{formatDate(o.completed_at || o.created_at)}</td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap">
                                        <button onClick={() => navigate(`/invoice/${o.id}`)} className="text-neutral-500 hover:text-neutral-900 mx-1" title="Open invoice" data-testid={`sale-invoice-${o.order_no}`}>
                                            <FileText size={14} />
                                        </button>
                                        <button onClick={() => shareInvoice(o)} className="text-neutral-500 hover:text-green-700 mx-1" title="WhatsApp invoice" data-testid={`sale-whatsapp-${o.order_no}`}>
                                            <WhatsappLogo size={14} weight="fill" />
                                        </button>
                                        {o.status === "completed" && o.payment_status !== "paid" && (
                                            <button onClick={() => setPaymentSaleId(o.id)} className="text-xs font-semibold text-green-700 hover:text-green-800 mr-2 ml-1"
                                                    data-testid={`sale-receive-payment-${o.order_no}`}>Receive</button>
                                        )}
                                        {o.status !== "completed" && o.status !== "cancelled" && (
                                            <button onClick={() => changeStatus(o, "completed")} className="text-xs font-semibold text-green-700 hover:text-green-800 mr-2"
                                                    data-testid={`sale-complete-${o.order_no}`}>Complete</button>
                                        )}
                                        {o.status !== "cancelled" && (
                                            <button onClick={() => changeStatus(o, "cancelled")} className="text-xs font-semibold text-red-600 hover:text-red-800"
                                                    data-testid={`sale-cancel-${o.order_no}`}>Cancel</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="sale-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-extrabold font-display">New Sale</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Customer</Label>
                            <Select value={form.customer_id || "walkin"} onValueChange={(v) => setForm({ ...form, customer_id: v === "walkin" ? "" : v })}>
                                <SelectTrigger className="mt-1.5" data-testid="sale-form-customer"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="walkin">Walk-in Customer</SelectItem>
                                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.mobile}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Status</Label>
                            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                <SelectTrigger className="mt-1.5" data-testid="sale-form-status"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="draft">Draft</SelectItem>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="completed">Completed (deducts stock)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Items */}
                    <div>
                        <div className="flex items-center justify-between mb-2 mt-2">
                            <div className="eyebrow">Line items</div>
                            <Button variant="outline" size="sm" onClick={addItem} data-testid="sale-form-add-item">
                                <Plus size={14} className="mr-1" /> Add item
                            </Button>
                        </div>
                        <div className="space-y-2">
                            {form.items.length === 0 && (
                                <div className="text-xs text-neutral-500 border border-dashed border-neutral-300 rounded p-4 text-center">No items yet — add one to continue.</div>
                            )}
                            {form.items.map((it, idx) => {
                                const p = products.find((x) => x.id === it.product_id);
                                return (
                                    <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-neutral-50 rounded p-2" data-testid={`sale-item-${idx}`}>
                                        <div className="col-span-12 md:col-span-5">
                                            <Select value={it.product_id} onValueChange={(v) => onProductSelect(idx, v)}>
                                                <SelectTrigger className="h-9"><SelectValue placeholder="Select product" /></SelectTrigger>
                                                <SelectContent>
                                                    {products.map((pp) => (
                                                        <SelectItem key={pp.id} value={pp.id}>
                                                            {pp.name} · <span className="text-xs opacity-60">Stock {pp.current_stock}</span>
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {p && <div className="text-[10px] text-neutral-500 mt-1">Stock available: {p.current_stock}</div>}
                                        </div>
                                        <Input type="number" placeholder="Qty" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} className="col-span-4 md:col-span-2 h-9" />
                                        <Input type="number" placeholder="Price" value={it.unit_price} onChange={(e) => updateItem(idx, { unit_price: e.target.value })} className="col-span-4 md:col-span-2 h-9" />
                                        <Input type="number" placeholder="GST%" value={it.gst_percent} onChange={(e) => updateItem(idx, { gst_percent: e.target.value })} className="col-span-3 md:col-span-2 h-9" />
                                        <button onClick={() => removeItem(idx)} className="col-span-1 text-neutral-500 hover:text-red-600 flex items-center justify-center" data-testid={`sale-item-remove-${idx}`}>
                                            <X size={16} />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Discount (₹)</Label>
                            <Input type="number" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} className="mt-1.5" data-testid="sale-form-discount" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Payment Status</Label>
                            <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
                                <SelectTrigger className="mt-1.5" data-testid="sale-form-payment"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="unpaid">Unpaid</SelectItem>
                                    <SelectItem value="partial">Partial</SelectItem>
                                    <SelectItem value="paid">Paid</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Notes</Label>
                        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1.5" data-testid="sale-form-notes" />
                    </div>

                    {/* Totals */}
                    <div className="bg-neutral-50 rounded p-4 mt-2">
                        <div className="flex justify-between text-sm py-1"><span className="text-neutral-500">Subtotal</span><span className="tabular font-semibold">{formatCurrency(totals.subtotal)}</span></div>
                        <div className="flex justify-between text-sm py-1"><span className="text-neutral-500">GST</span><span className="tabular font-semibold">{formatCurrency(totals.tax)}</span></div>
                        <div className="flex justify-between text-sm py-1"><span className="text-neutral-500">Discount</span><span className="tabular font-semibold">-{formatCurrency(form.discount)}</span></div>
                        <div className="flex justify-between text-lg py-2 mt-2 border-t border-neutral-200"><span className="font-bold font-display">Total</span><span className="tabular font-extrabold font-display" style={{ color: "#15803D" }}>{formatCurrency(totals.total)}</span></div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="sale-form-cancel">Cancel</Button>
                        <Button onClick={submit} disabled={saving} className="text-white" style={{ backgroundColor: "#15803D" }} data-testid="sale-form-submit">
                            {saving ? "Saving..." : "Record Sale"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ReceivePaymentDialog
                open={!!paymentSaleId}
                onOpenChange={(v) => !v && setPaymentSaleId(null)}
                defaultSaleId={paymentSaleId || ""}
                onSaved={(p) => { setPaymentSaleId(null); load(); navigate(`/receipt/${p.id}`); }}
            />
        </>
    );
}

function StatusPill({ label, styleMap }) {
    const s = styleMap[label] || { bg: "#F3F4F6", fg: "#374151" };
    return (
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded" style={{ background: s.bg, color: s.fg }}>
            {label}
        </span>
    );
}
